import { FC, useCallback, useEffect, useRef, useState } from "react";
import {
  Card,
  Row,
  Col,
  Input,
  Button,
  Typography,
  Table,
  Tag,
  Radio,
  Modal,
  Spin,
  Form,
  Flex,
  Pagination,
  Tooltip,
  Badge,
} from "antd";
import {
  errorMessage,
  successMessage,
  warningMessage,
} from "../../../utils/notificationUtils";
import { getSchemeDisplayName } from "../../../utils/schemeDisplay";
import {
  getCaptcha,
  getLiveScreen,
  portalsLogin,
  portalsLogout,
  portalsSaveOtp,
} from "../../../apis/authService";
import {
  AppstoreOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  DownloadOutlined,
  LockOutlined,
  LoginOutlined,
  ReloadOutlined,
  SwapOutlined,
  SyncOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import {
  fetchLoginHistory,
  fetchPortalDataForCGHSNewForEODBOD,
  fetchPortalDataForEchsForEODBOD,
  fetchProcessHistory,
} from "../../../apis/reconciliationService";
import LoginHistoryModal from "./LoginHistoryModal";

const { Text } = Typography;

interface FetchSchemeActionsProps {
  selectedScheme: number | null;
  availableSchemes: any;
  selectedPortal: any;
  handleSchemeChange: any;
  selectedSchemeName: string;
  handleSummaryClicked: (record: any) => void;
  handleViewMoreClick: () => void;
  selectedHospital: number | null;
  bodEod: any;
  selectedDate: any;
  initialEODBODConfig: any;
  getInitialEODBODConfig: any;
  activeTab: any;
}

const FetchSchemeActions: FC<FetchSchemeActionsProps> = ({
  availableSchemes,
  selectedScheme,
  selectedPortal,
  handleSchemeChange,
  selectedSchemeName,
  handleSummaryClicked,
  handleViewMoreClick,
  selectedHospital,
  bodEod,
  selectedDate,
  initialEODBODConfig,
  getInitialEODBODConfig,
  activeTab,
}) => {
  const [echsPortalUrl, setEchsPortalUrl] = useState<any>(null);
  const echsPortalUrlRef = useRef(null);
  const [showCaptcha, setShowCaptcha] = useState(false);
  const [isCaptchaLoading, setIsCaptchaLoading] = useState<boolean>(false);
  const [fetchType, setFetchType] = useState<string>("all");
  const [isLiveScreenModalOpen, setIsLiveScreenModalOpen] = useState(false);
  const [liveScreenUrl, setLiveScreenUrl] = useState<any>(null);
  const [isLiveScreenLoading, setIsLiveScreenLoading] = useState(false);
  const [captchaCodeText1, setCaptchaCodeText1] = useState<any>(null);
  const [captchaCodeText2, setCaptchaCodeText2] = useState<any>(null);
  const [captchaImageUrl, setCaptchaImageUrl] = useState<any>(null);
  const [captchaImageUrl2, setCaptchaImageUrl2] = useState<any>(null);
  const [isLoggedInLoading, setIsLoggedInLoading] = useState<boolean>(false);
  const [isLoggedOutLoading, setIsLoggedOutLoading] = useState<boolean>(false);
  const [message, setMessage] = useState<string>("");
  const [statusLogin, setStatusLogin] = useState<string>("");
  const [showLoginButton, setShowLoginButton] = useState<boolean>(true);
  const [isOtpLoading, setIsOtpLoading] = useState<boolean>(false);
  const [showOtpField, setShowOtpField] = useState<boolean>(false);
  const [isBrowserSessionActive, setIsBrowserSessionActive] =
    useState<boolean>(false);
  const [mobileNumber, setMobileNumber] = useState<string>("");
  const [isFetchPortalDataLoading, setIsFetchPortalDataLoading] =
    useState<boolean>(false);
  const [fetchPortalDataApiMessage, setFetchPortalDataApiMessage] = useState<
    string | null
  >(null);
  const [fetchPortalDataApiMessageStatus, setFetchPortalDataApiMessageStatus] =
    useState<string | null>(null);
  const [isLoggedOut, setIsLoggedOut] = useState(false);
  const [processHistoryData, setProcessHistoryData] = useState<any>([]);
  const [isProcessHistoryLoading, setIsProcessHistoryLoading] =
    useState<boolean>(false);
  const [openLoginHistoryModal, setOpenLoginHistoryModal] =
    useState<boolean>(false);
  const [isLoginHistoryModalDataLoading, setIsLoginHistoryModalDataLoading] =
    useState<boolean>(false);
  const [loginHistoryModalData, setloginHistoryModalData] = useState<any[]>([]);
  const [loginHistoryDataTotal, setLoginHistoryDataTotal] = useState<number>(0);
  const [isRefreshLoading, setIsRefreshLoading] = useState<boolean>(false);

  const onRefreshClick = async () => {
    try {
      setIsRefreshLoading(true);

      setProcessHistoryPagination((prev) => ({
        ...prev,
        current: 1,
      }));

      await getProcessHistory(1, processHistoryPagination.pageSize);
    } catch (err) {
      errorMessage("Failed to refresh data");
    } finally {
      setIsRefreshLoading(false);
    }
  };

  const [processHistoryPagination, setProcessHistoryPagination] = useState({
    current: 1,
    pageSize: 10,
  });
  const [processHistoryTotal, setProcessHistoryTotal] = useState<number>(0);

  const [form] = Form.useForm();

  const onFinish = async (values: any) => {
    await handleSaveOtp(values, captchaCodeText2);
    form.resetFields();
  };

  enum SchemName {
    CGHS = "CGHS",
    CGHS_NEW = "CGHS NEW",
    ECHS = "ECHS",
    AYUSHMAN_BHARAT = "AYUSHMAN BHARAT",
  }

  const resetData = useCallback(() => {
    setCaptchaCodeText1(null);
    setCaptchaImageUrl(null);
    setShowOtpField(false);
    setShowLoginButton(true);
    setMessage("");
    setStatusLogin("");
    setIsBrowserSessionActive(false);
    setMobileNumber("");
    setCaptchaCodeText2(null);
    setCaptchaImageUrl2(null);
    setShowCaptcha(false);
  }, []);

  useEffect(() => {
    setShowOtpField(false);
    setShowLoginButton(true);
    // setSelectedPortal(null);
    setMessage("");
    setStatusLogin("");
    setIsBrowserSessionActive(false);
    setCaptchaCodeText1(null);
    setCaptchaImageUrl(null);

    setCaptchaCodeText2("");
    setCaptchaImageUrl2(null);
    setMobileNumber("");
    setIsLoggedOut(false);
  }, [selectedScheme]);

  useEffect(() => {
    if (
      selectedSchemeName === SchemName.AYUSHMAN_BHARAT ||
      selectedSchemeName === SchemName.CGHS_NEW
    ) {
      setShowCaptcha(true);
      fetchCaptcha();
    }
    setEchsPortalUrl(null);
    echsPortalUrlRef.current = null;
  }, [selectedSchemeName]);

  useEffect(() => {
    if (selectedHospital && bodEod) {
      getProcessHistory();
    } else {
      setProcessHistoryData([]);
      setProcessHistoryTotal(0);
    }
  }, [selectedHospital, bodEod]);

  const handleSaveLogin = useCallback(async () => {
    if (!selectedHospital || !selectedScheme) {
      warningMessage("Please Select Hospital and Scheme");
      return;
    }
    try {
      setMessage("");
      setIsLoggedInLoading(true);
      warningMessage("Login is in progress");
      if (selectedSchemeName?.toUpperCase() === "CGHS") {
        setTimeout(() => {
          setShowOtpField(true);
        }, 12000);
      }
      const data = {
        schemeId: selectedScheme,
        hospitalId: selectedHospital,
        captchaCode: captchaCodeText1,
        // ...(isAyushman && !showOtpField ? { mobileNumber } : {}),
        ...(selectedSchemeName === SchemName.ECHS && {
          portalUrl: echsPortalUrlRef.current,
        }),
        portalLoginEodBodRequest: {
          eodBodProcessType: bodEod,
          targetDate: selectedDate.startOf().format("YYYY-MM-DDTHH:mm:ss.SSS"),
        },
      };
      const res = await portalsLogin(data, selectedPortal);
      setIsLoggedInLoading(false);
      if (res) {
        setShowOtpField(!!res.otpRequired);
        setShowLoginButton(false);

        if (
          selectedSchemeName === SchemName.AYUSHMAN_BHARAT &&
          res.otpRequired
        ) {
          setCaptchaImageUrl2(res?.secondCaptchaImage || res?.captcha || null);
        }

        if (selectedSchemeName === SchemName.CGHS_NEW && res.otpRequired) {
          setCaptchaImageUrl2(res?.secondCaptchaImage || res?.captcha || null);
        }

        if (
          (selectedSchemeName === SchemName.CGHS &&
            res.status?.toUpperCase() === "REJECTED") ||
          res.status?.toUpperCase() === "PENDING"
        ) {
          setMessage("");
        }

        if (
          selectedSchemeName === SchemName.AYUSHMAN_BHARAT &&
          !res.otpRequired &&
          res.status?.toUpperCase() === "REJECTED"
        ) {
          setCaptchaCodeText1("");
          setShowLoginButton(true);
          setCaptchaImageUrl(res?.captcha);
          setCaptchaCodeText2("");
          setMessage(res?.message || "");
        }

        if (
          selectedSchemeName === SchemName.CGHS_NEW &&
          !res.otpRequired &&
          res.status?.toUpperCase() === "REJECTED"
        ) {
          setCaptchaCodeText1("");
          setShowLoginButton(true);
          setCaptchaImageUrl(res?.captcha);
          setCaptchaCodeText2("");
          setMessage(res?.message || "");
        }

        if (!res.otpRequired) {
          setMessage(res.message || "");
          setStatusLogin(res.status || "");
          //   setSelectedScheme(null);
          //   setSelectedPortal(null);
        }

        if (res.status?.toUpperCase() !== "REJECTED") {
          // setShowCaptcha(false);
          // setImageUrl(null);
          successMessage("Logged In Successfully");
          getInitialEODBODConfig();
        }
        // fetchLoginSession();
      } else if (selectedSchemeName === SchemName.CGHS_NEW) {
        setStatusLogin("Success");
        setShowLoginButton(false);
      }
    } catch (err: any) {
      errorMessage(err?.message || "Please, Try again later");
      setIsLoggedInLoading(false);
      setShowLoginButton(true);
      setShowOtpField(false);
    }
  }, [
    captchaCodeText1,
    selectedHospital,
    selectedPortal,
    selectedScheme,
    selectedSchemeName,
  ]);

  const handleLogout = useCallback(async () => {
    if (!selectedHospital || !selectedScheme) {
      warningMessage("Please Select Hospital and Scheme");
      return;
    }
    try {
      const data = {
        schemeId: selectedScheme,
        hospitalId: selectedHospital,
        // ...(isAyushman ? { mobileNumber } : {}),
      };
      setIsLoggedOutLoading(true);
      const res = await portalsLogout(data, selectedPortal);
      setIsLoggedOutLoading(false);
      successMessage(res?.message || "Logout done successfully");
      resetData();
      setIsLoggedOut(true);
    } catch (err: any) {
      errorMessage(err?.message || "Please, Try again later");
      setIsLoggedOutLoading(false);
    }
  }, [selectedHospital, selectedScheme]);

  const handleSaveOtp = useCallback(
    async (values: any, currentCaptchaCode2: any) => {
      if (!selectedHospital || !selectedScheme) {
        warningMessage("Please Select Hospital and Scheme");
        return;
      }
      try {
        const data = {
          schemeId: selectedScheme,
          hospitalId: selectedHospital,
          otp: values?.otp,
          captchaCode: currentCaptchaCode2,
          portalLoginEodBodRequest: {
            eodBodProcessType: bodEod,
            targetDate: selectedDate
              .startOf()
              .format("YYYY-MM-DDTHH:mm:ss.SSS"),
          },
        };
        setIsOtpLoading(true);
        const res = await portalsSaveOtp(data, selectedPortal);
        setIsOtpLoading(false);
        if (
          selectedSchemeName === SchemName.AYUSHMAN_BHARAT &&
          res.status?.toUpperCase() === "REJECTED"
        ) {
          setCaptchaCodeText2("");
          setCaptchaImageUrl2(res?.captcha);
        }

        if (
          selectedSchemeName === SchemName.CGHS_NEW &&
          res.status?.toUpperCase() === "REJECTED"
        ) {
          setCaptchaCodeText2("");
          setCaptchaImageUrl2(res?.captcha);
        }

        if (res.status?.toUpperCase() !== "REJECTED") {
          setShowCaptcha(false);
          setCaptchaImageUrl(null);
          successMessage("OTP submitted successfully");
          setShowOtpField(false);
          getInitialEODBODConfig();
        }
        setMessage(res.message || "");
        setStatusLogin(res.status || "");
        // setSelectedScheme(null);
        // setSelectedPortal(null);
        // fetchLoginSession();
      } catch (err: any) {
        errorMessage(err?.message || "Please, Try again later");
        setIsOtpLoading(false);
      }
    },
    [selectedHospital, selectedScheme, selectedSchemeName],
  );

  const Base64Image = ({ base64Data }: { base64Data: string }) => {
    const imageSrc = `data:image/png;base64,${base64Data}`;

    return (
      <img
        src={imageSrc}
        alt="From Base64"
        style={{
          width: "240px",
          borderRadius: "8px",
          boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
        }}
      />
    );
  };

  const LiveScreenBase64Image = ({ base64Data }: { base64Data: string }) => {
    const imageSrc = `data:image/png;base64,${base64Data}`;

    return (
      <img
        src={imageSrc}
        alt="From Base64"
        style={{ width: "100%", borderRadius: "10px" }}
      />
    );
  };

  const handleViewLiveScreen = async () => {
    setIsLiveScreenLoading(true);
    const url = await fetchLiveScreen();
    setLiveScreenUrl(url);
    setIsLiveScreenModalOpen(true);
    setIsLiveScreenLoading(false);
  };

  const fetchLiveScreen = async () => {
    try {
      if (!selectedHospital || !selectedScheme) {
        warningMessage("Please Select Hospital and Scheme");
        return;
      }
      const data = {
        schemeId: selectedScheme,
        hospitalId: selectedHospital,
        ...(selectedSchemeName === "ECHS" && {
          portalUrl: echsPortalUrlRef.current,
        }),
      };
      const res = await getLiveScreen(data, selectedPortal);
      return res?.message;
    } catch (err: any) {
      errorMessage(err?.message);
    }
  };

  const fetchCaptcha = async () => {
    setIsCaptchaLoading(true);
    try {
      if (!selectedHospital || !selectedScheme) {
        warningMessage("Please Select Hospital and Scheme");
        return;
      }

      const data = {
        schemeId: selectedScheme,
        hospitalId: selectedHospital,
        ...(selectedSchemeName === "ECHS" && {
          portalUrl: echsPortalUrlRef.current,
        }),
      };

      const res = await getCaptcha(data, selectedPortal);
      setCaptchaImageUrl(res?.captcha);
      setIsCaptchaLoading(false);
    } catch (err: any) {
      errorMessage(err?.message);
      setIsCaptchaLoading(false);
    }
  };

  const schemeOptions = availableSchemes
    ?.filter((scheme: any) =>
      [SchemName.CGHS_NEW, SchemName.ECHS].includes(
        scheme?.schemeMetaDataName?.toUpperCase(),
      ),
    )
    ?.map((scheme: any) => {
      const { portalMetaDataIdsList, ...rest } = scheme;
      return {
        label: getSchemeDisplayName(rest),
        value: rest.schemeMetaDataId,
        portalMetaDataIdsList,
      };
    });

  const handleEchsPortalUrlChange = (value: any) => {
    setEchsPortalUrl(value);
    echsPortalUrlRef.current = value;
    setShowCaptcha(true);
    fetchCaptcha();
  };

  const onSchemeChange = (
    schemeId: number,
    schemeName: string,
    portalId: any,
  ) => {
    handleSchemeChange(schemeId, schemeName, portalId);
    setFetchPortalDataApiMessage(null);
    setFetchPortalDataApiMessageStatus(null);
  };

  const fetchPortalData = async () => {
    try {
      setIsFetchPortalDataLoading(true);
      setFetchPortalDataApiMessage(null);

      if (
        selectedSchemeName === SchemName.ECHS &&
        selectedHospital &&
        selectedScheme
      ) {
        const res = await fetchPortalDataForEchsForEODBOD(
          selectedHospital,
          selectedScheme,
          fetchType,
          initialEODBODConfig?.orchestratorId || -1,
        );
        setFetchPortalDataApiMessage(res?.message);
        setFetchPortalDataApiMessageStatus(res?.status);
      } else if (selectedHospital && selectedScheme) {
        const res = await fetchPortalDataForCGHSNewForEODBOD(
          selectedHospital,
          selectedScheme,
          fetchType,
          initialEODBODConfig?.orchestratorId || -1,
        );
        setFetchPortalDataApiMessage(res?.message);
        setFetchPortalDataApiMessageStatus(res?.status);
      }
      getInitialEODBODConfig();
    } catch (error: any) {
      errorMessage(error?.message || "Unable to fetch data. Please try again.");
    } finally {
      setIsFetchPortalDataLoading(false);
    }
  };

  const getProcessHistory = async (page: number = 1, size: number = 10) => {
    try {
      if (!selectedHospital) {
        warningMessage("Please Select Hospital");
        return;
      }
      setIsProcessHistoryLoading(true);

      const data: any = {
        // schemeId: selectedScheme,
        hospitalId: selectedHospital,
        page: page - 1,
        size,
      };

      if (fetchType === "all") {
        data.fetchType = "ALL";
      } else if (fetchType === "status") {
        data.fetchType = "STATUS";
      } else if (fetchType === "query") {
        data.fetchType = "QUERY";
      } else if (fetchType === "settlement") {
        data.fetchType = "SETTLEMENT";
      }

      const response = await fetchProcessHistory(data);
      setProcessHistoryData(response?.data?.data || []);
      setProcessHistoryTotal(response?.data?.totalElements || 0);
    } catch (err) {
      setProcessHistoryData([]);
      setProcessHistoryTotal(0);
      errorMessage((err as any)?.message || "Failed to load process history");
    } finally {
      setIsProcessHistoryLoading(false);
    }
  };

  const renderValue = (value: any) =>
    value === null || value === undefined || value === "" ? "-" : value;

  const onViewMoreClick = () => {
    handleViewMoreClick();
  };

  const handleLoginHistoryModalPaginationChange = (
    page: number,
    size: number,
  ) => {
    const payload = {
      hospitalId: selectedHospital,
      schemeId: selectedScheme,
      page: page - 1,
      size,
    };

    fetchLoginHistoryModalData(payload);
  };

  const onViewLoginHistoryClicked = () => {
    setOpenLoginHistoryModal(true);
    const payload = {
      hospitalId: selectedHospital,
      // schemeId: selectedScheme,
      // fetchType,
      // "status": "FAILED",
      // "performedBy": 1,
      // "startDate": "2026-03-01T00:00:00",
      // "endDate": "2026-03-20T23:59:59",
      page: 0,
      size: 10,
    };

    fetchLoginHistoryModalData(payload);
  };

  const fetchLoginHistoryModalData = async (payload: any) => {
    try {
      setIsLoginHistoryModalDataLoading(true);
      const response = await fetchLoginHistory(payload);
      setloginHistoryModalData(response?.data?.data || []);
      setLoginHistoryDataTotal(response?.data?.totalElements || 0);
    } catch (error: any) {
      const messageText =
        error?.message || "Failed to load process history for View More";
      errorMessage(messageText);
      setloginHistoryModalData([]);
      setLoginHistoryDataTotal(0);
    } finally {
      setIsLoginHistoryModalDataLoading(false);
    }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      <Card
        title={
          <span style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <AppstoreOutlined style={{ color: "rgb(0, 76, 143)" }} />
            Scheme Selection
          </span>
        }
        extra={
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <a onClick={() => onViewLoginHistoryClicked()}>
              View Login History
            </a>
          </div>
        }
      >
        <Row gutter={12}>
          {schemeOptions.map((item: any) => (
            <Col span={4} key={item.value}>
              <Card
                hoverable
                onClick={() =>
                  onSchemeChange(
                    item?.value,
                    item?.label,
                    item.portalMetaDataIdsList?.[0],
                  )
                }
                style={{
                  textAlign: "center",
                  padding: "6px",
                  height: "45px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  border:
                    selectedScheme === item.value
                      ? "2px solid rgb(0, 76, 143)"
                      : "1px solid #e5e5e5",
                  background:
                    selectedScheme === item.value ? "#f0f6ff" : "#fff",
                  cursor: "pointer",
                  fontSize: "13px",
                }}
              >
                <Text strong>{item.label}</Text>
              </Card>
            </Col>
          ))}
        </Row>
      </Card>

      {selectedScheme && (
        <Card
          title={
            <span style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <LockOutlined style={{ color: "rgb(0, 76, 143)" }} />
              {selectedSchemeName} Login Portal
            </span>
          }
        >
          {selectedSchemeName === SchemName.ECHS && (
            <div style={{ marginBottom: 20 }}>
              <Text
                strong
                style={{ fontSize: 14, display: "block", marginBottom: 8 }}
              >
                ECHS Portal URL
              </Text>
              <Radio.Group
                style={{ display: "flex", gap: 16 }}
                value={echsPortalUrl}
                onChange={(e) => handleEchsPortalUrlChange(e.target.value)}
              >
                <Radio
                  value="https://www.echsbpa.utiitsl.com/ECHS/"
                  style={{ color: "#374151" }}
                  disabled={
                    showCaptcha &&
                    captchaImageUrl &&
                    echsPortalUrl !== "https://www.echsbpa.utiitsl.com/ECHS/"
                  }
                >
                  <span style={{ fontWeight: 600, color: "#374151" }}>
                    MAIN URL (https://www.echsbpa.utiitsl.com/ECHS/)
                  </span>
                </Radio>
                <Radio
                  value="https://www.echsbpa1.utiitsl.com/ECHS/"
                  style={{ color: "#374151" }}
                  disabled={
                    showCaptcha &&
                    captchaImageUrl &&
                    echsPortalUrl !== "https://www.echsbpa1.utiitsl.com/ECHS/"
                  }
                >
                  <span style={{ fontWeight: 600, color: "#374151" }}>
                    ALTERNATE URL (https://www.echsbpa1.utiitsl.com/ECHS/)
                  </span>
                </Radio>
              </Radio.Group>
            </div>
          )}

          <Spin spinning={isCaptchaLoading} tip="Loading Captcha...">
            <Row gutter={[16, 16]} align="middle" style={{ marginBottom: 20 }}>
              {showCaptcha && (
                <>
                  {/* Captcha 1 */}
                  <Col>
                    {captchaImageUrl && (
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                        }}
                      >
                        {selectedSchemeName === SchemName.ECHS && (
                          <SyncOutlined
                            title="Refresh Captcha"
                            onClick={fetchCaptcha}
                            style={{ cursor: "pointer" }}
                          />
                        )}

                        <Base64Image base64Data={captchaImageUrl} />

                        <Input
                          placeholder="Enter Captcha"
                          value={captchaCodeText1}
                          onChange={(e) => setCaptchaCodeText1(e.target.value)}
                          style={{ width: 140, textTransform: "none" }}
                        />
                      </div>
                    )}
                  </Col>

                  {/* Captcha 2 (same condition, no change) */}
                  {showOtpField && (
                    <>
                      {(selectedSchemeName === SchemName.AYUSHMAN_BHARAT ||
                        selectedSchemeName === SchemName.CGHS_NEW) &&
                        captchaImageUrl2 && (
                          <Col>
                            <Spin
                              spinning={isCaptchaLoading}
                              tip="Loading Captcha..."
                            >
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 8,
                                }}
                              >
                                <Base64Image base64Data={captchaImageUrl2} />

                                <Input
                                  placeholder="Enter Captcha"
                                  value={captchaCodeText2}
                                  onChange={(e) =>
                                    setCaptchaCodeText2(e.target.value)
                                  }
                                  style={{ width: 140, textTransform: "none" }}
                                />
                              </div>
                            </Spin>
                          </Col>
                        )}

                      {/* OTP + Button (same logic) */}
                      <Col style={{ marginTop: 16 }}>
                        <Form form={form} layout="inline" onFinish={onFinish}>
                          <Form.Item
                            label={
                              <span
                                style={{ fontWeight: "500", fontSize: "16px" }}
                              >
                                OTP
                              </span>
                            }
                            name="otp"
                            rules={[
                              { required: true, message: "OTP is required" },
                            ]}
                          >
                            <Input
                              placeholder="Enter OTP"
                              style={{ width: 160 }}
                            />
                          </Form.Item>

                          <Form.Item>
                            <Button
                              type="primary"
                              loading={isOtpLoading}
                              htmlType="submit"
                              style={{
                                color: "#FFFFFF",
                                backgroundColor: "#008B8B",
                                fontWeight: 700,
                                borderRadius: "8px",
                                padding: "0 20px",
                              }}
                            >
                              Submit
                            </Button>
                          </Form.Item>
                        </Form>
                      </Col>
                    </>
                  )}
                </>
              )}
            </Row>
          </Spin>

          {message && (
            <div style={{ width: "100%", marginTop: "4px" }}>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Text
                  style={{
                    color:
                      statusLogin?.toLowerCase()?.includes("failed") ||
                      statusLogin?.toLowerCase()?.includes("reject")
                        ? "red"
                        : "green",
                    fontWeight: 600,
                  }}
                >
                  {message}
                </Text>
              </div>
            </div>
          )}

          <div
            style={{
              width: "100%",
              display: "flex",
              gap: "12px",
              // marginTop: "auto",
              flexWrap: "wrap",
              justifyContent: "flex-start",
              alignItems: "center",
              marginTop: 10,
            }}
          >
            <Button
              type="default"
              loading={isLiveScreenLoading}
              disabled={isLiveScreenLoading}
              style={{
                color: "#FFFFFF",
                backgroundColor: "#004C8F",
                borderRadius: "8px",
                padding: "6px 14px",
                fontWeight: 600,
              }}
              onClick={handleViewLiveScreen}
            >
              View Live Screen
            </Button>

            {showCaptcha ? (
              selectedHospital &&
              selectedScheme &&
              showLoginButton &&
              captchaCodeText1 ? (
                <Col>
                  <Button
                    icon={<LoginOutlined />}
                    style={{ backgroundColor: "#004C8F", color: "#fff" }}
                    onClick={handleSaveLogin}
                    loading={isLoggedInLoading}
                  >
                    {(selectedSchemeName === SchemName.AYUSHMAN_BHARAT ||
                      selectedSchemeName === SchemName.CGHS_NEW) &&
                    !showOtpField
                      ? "Verify"
                      : "Login"}
                  </Button>
                </Col>
              ) : (
                <></>
              )
            ) : selectedHospital &&
              selectedScheme &&
              showLoginButton &&
              selectedSchemeName !== SchemName.ECHS &&
              !isLoggedOut ? (
              <Col>
                <Button
                  icon={<LoginOutlined />}
                  style={{ backgroundColor: "#004C8F", color: "#fff" }}
                  onClick={handleSaveLogin}
                  loading={isLoggedInLoading}
                >
                  {(selectedSchemeName === SchemName.AYUSHMAN_BHARAT ||
                    selectedSchemeName === SchemName.CGHS_NEW) &&
                  !showOtpField
                    ? "Verify"
                    : "Login"}
                </Button>
              </Col>
            ) : (
              <></>
            )}

            {!showLoginButton &&
              !statusLogin?.toLowerCase()?.includes("failed") &&
              !statusLogin?.toLowerCase()?.includes("reject") && (
                <Button
                  type="default"
                  style={{
                    backgroundColor: "#D51E1E",
                    color: "white",
                    borderRadius: "8px",
                    padding: "6px 14px",
                    fontWeight: 600,
                  }}
                  onClick={handleLogout}
                  loading={isLoggedOutLoading}
                >
                  Log Out
                </Button>
              )}

            {statusLogin?.toLowerCase()?.includes("failed") ||
            statusLogin?.toLowerCase()?.includes("reject")
              ? !(
                  selectedSchemeName === SchemName.AYUSHMAN_BHARAT ||
                  selectedSchemeName === SchemName.CGHS_NEW
                ) && (
                  <Button
                    type="default"
                    style={{
                      color: "white",
                      backgroundColor: "#D51E1E",
                      borderRadius: "8px",
                      padding: "6px 14px",
                      fontWeight: 600,
                    }}
                    onClick={handleLogout}
                  >
                    Retry
                  </Button>
                )
              : null}
          </div>
        </Card>
      )}

      {/* Fetch Actions */}
      <Card
        title={
          <span style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <SwapOutlined style={{ color: "rgb(0, 76, 143)" }} />
            Fetch Actions
          </span>
        }
      >
        <Row gutter={10}>
          {[
            { label: "Settlement", value: "settlement" },
            { label: "Query", value: "query" },
            { label: "Status", value: "status" },
            { label: "All", value: "all" },
          ].map((item) => (
            <Col span={3} key={item.value}>
              <Card
                hoverable={item.value === "all"}
                onClick={() => item.value === "all" && setFetchType(item.value)}
                style={{
                  textAlign: "center",
                  padding: "4px",
                  height: "38px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  border:
                    fetchType === item.value
                      ? "2px solid rgb(0, 76, 143)"
                      : "1px solid #d9d9d9",
                  background: fetchType === item.value ? "#f0f6ff" : "#f5f5f5",
                  cursor: fetchType === item.value ? "pointer" : "not-allowed",
                  fontSize: "12px",
                  opacity: fetchType === item.value ? 1 : 0.6,
                }}
              >
                <Text strong>{item.label}</Text>
              </Card>
            </Col>
          ))}
        </Row>

        <Row style={{ marginTop: 30 }}>
          <Col>
            <div style={{ display: "flex", alignItems: "center", gap: 40 }}>
              <Button
                icon={<DownloadOutlined />}
                style={{ backgroundColor: "#004C8F", color: "#fff" }}
                loading={isFetchPortalDataLoading}
                disabled={
                  !selectedHospital ||
                  !selectedScheme 
                  // || !!fetchPortalDataApiMessage  //was uncomment need to uncomment when portal active or inactive is not done
                  // || !initialEODBODConfig?.showFetchActions
                }
                onClick={fetchPortalData}
              >
                Fetch Data
              </Button>

              {fetchPortalDataApiMessage && (
                <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                  {fetchPortalDataApiMessageStatus?.toUpperCase() ===
                  "SUCCESS" ? (
                    <CheckCircleOutlined style={{ color: "#52c41a" }} />
                  ) : (
                    <CloseCircleOutlined style={{ color: "#ff4d4f" }} />
                  )}

                  <Typography.Text
                    style={{
                      color:
                        fetchPortalDataApiMessageStatus?.toUpperCase() ===
                        "SUCCESS"
                          ? "#52c41a"
                          : "#ff4d4f",
                      fontWeight: 500,
                    }}
                  >
                    {fetchPortalDataApiMessage}
                  </Typography.Text>
                </div>
              )}
            </div>
          </Col>
        </Row>
      </Card>

      <Card
        title="Process History"
        extra={
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <Tooltip title="Refresh data">
              <Button
                icon={<ReloadOutlined />}
                size="small"
                style={{
                  backgroundColor: "#004C8F",
                  color: "#fff",
                  borderRadius: "7px",
                  padding: "6px 14px",
                  fontWeight: 500,
                }}
                onClick={onRefreshClick}
                loading={isRefreshLoading}
              >
                Refresh
              </Button>
            </Tooltip>

            <a onClick={() => onViewMoreClick()}>View More</a>
          </div>
        }
      >
        <>
          <Table
            loading={isProcessHistoryLoading}
            dataSource={processHistoryData}
            columns={[
              { title: "Scheme", dataIndex: "schemeName", render: renderValue },
              {
                title: "Fetch Actions",
                dataIndex: "fetchType",
                render: renderValue,
              },
              {
                title: "Initiated By",
                dataIndex: "performedByName",
                render: renderValue,
              },
              {
                title: "Timestamp",
                dataIndex: "executedAt",
                render: (_: any, record: any) => {
                  return record?.executedAt
                    ? dayjs
                        .utc(record.executedAt)
                        .local()
                        .format("DD-MM-YYYY hh:mm A")
                    : "-";
                },
              },
              {
                title: "Status",
                dataIndex: "status",
                render: (status: string) => (
                  // <Tag
                  //   color={
                  //     status?.toLowerCase() === "success" ? "green" : "orange"
                  //   }
                  // >
                  //   {status ?? "-"}
                  // </Tag>
                  <Tag
                    color={
                      status?.toLowerCase() === "success" ? "green" : "orange"
                    }
                    style={{
                      borderRadius: "999px",
                      padding: "2px 12px",
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 6,
                    }}
                  >
                    <Badge
                      color={
                        status?.toLowerCase() === "success" ? "green" : "orange"
                      }
                    />
                    {status ?? "-"}
                  </Tag>
                ),
              },
              {
                title: "Summary",
                render: (_: any, record: any) => {
                  const isSuccess =
                    record?.status?.trim().toLowerCase() === "success";

                  return (
                    <Flex gap={8}>
                      <span
                        style={{
                          color: isSuccess ? "#00549D" : "#bfbfbf",
                          cursor: isSuccess ? "pointer" : "not-allowed",
                        }}
                      >
                        <a
                          onClick={() => {
                            if (!isSuccess) return;
                            handleSummaryClicked(record);
                          }}
                          style={{
                            pointerEvents: isSuccess ? "auto" : "none",
                            color: "inherit",
                            textDecoration: "none",
                          }}
                        >
                          Summary
                        </a>
                      </span>
                    </Flex>
                  );
                },
              },
            ]}
            pagination={{
              current: processHistoryPagination.current,
              pageSize: processHistoryPagination.pageSize,
              total: processHistoryTotal,
              showSizeChanger: true,
              pageSizeOptions: ["10", "20", "50", "100"],
              onChange: (page, pageSize) => {
                setProcessHistoryPagination({ current: page, pageSize });
                getProcessHistory(page, pageSize);
              },
            }}
            rowClassName={(record, index) =>
              index % 2 === 0 ? "ant-table-row-light" : ""
            }
          />
        </>
      </Card>

      <Modal
        title="Live Screen"
        open={isLiveScreenModalOpen}
        footer={null}
        onCancel={() => setIsLiveScreenModalOpen(false)}
        width={1000}
      >
        {liveScreenUrl ? (
          selectedSchemeName === SchemName.ECHS ||
          selectedSchemeName === SchemName.CGHS_NEW ? (
            <LiveScreenBase64Image base64Data={liveScreenUrl} />
          ) : (
            <img
              src={liveScreenUrl}
              alt="Live Screen"
              style={{ width: "100%", borderRadius: "10px" }}
            />
          )
        ) : (
          <p>No Live Screen Available</p>
        )}
      </Modal>

      <LoginHistoryModal
        open={openLoginHistoryModal}
        onClose={() => setOpenLoginHistoryModal(false)}
        isLoginHistoryModalDataLoading={isLoginHistoryModalDataLoading}
        loginHistoryModalData={loginHistoryModalData}
        loginHistoryDataTotal={loginHistoryDataTotal}
        onPaginationChange={handleLoginHistoryModalPaginationChange}
      />
    </div>
  );
};

export default FetchSchemeActions;
